"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.progression = exports.getChordsByProgression = exports.getChordDegrees = void 0;
var harmonics_1 = require("harmonics");
var utils_1 = require("./utils");
/**
 * Get the chords that go with a given scale/mode
 * This is useful only in case you want to check what chords work with a scale/mode
 * so that you can come up with chord progressions
 * @param  {String} mode e.g. major
 * @return {Array} e.g.['I', 'ii', 'iii', 'IV', 'V', 'vi', 'vii°']
 */
var getChordDegrees = function (mode) {
    var theRomans = {
        ionian: ['I', 'ii', 'iii', 'IV', 'V', 'vi', 'vii°'],
        dorian: ['i', 'ii', 'III', 'IV', 'v', 'vi°', 'VII'],
        phrygian: ['i', 'II', 'III', 'iv', 'v°', 'VI', 'vii'],
        lydian: ['I', 'II', 'iii', 'iv°', 'V', 'vi', 'vii'],
        mixolydian: ['I', 'ii', 'iii°', 'IV', 'v', 'vi', 'VII'],
        aeolian: ['i', 'ii°', 'III', 'iv', 'v', 'VI', 'VII'],
        locrian: ['i°', 'II', 'iii', 'iv', 'V', 'VI', 'vii'],
        'melodic minor': ['i', 'ii', 'III+', 'IV', 'V', 'vi°', 'vii°'],
        'harmonic minor': ['i', 'ii°', 'III+', 'iv', 'V', 'VI', 'vii°'],
    };
    theRomans.major = theRomans.ionian;
    theRomans.minor = theRomans.aeolian;
    return theRomans[mode] || [];
};
exports.getChordDegrees = getChordDegrees;
var idxByDegree = {
    i: 0,
    ii: 1,
    iii: 2,
    iv: 3,
    v: 4,
    vi: 5,
    vii: 6,
};
/**
 * Get a chord name from degree
 * @param  {String} roman e.g. ii OR ii° OR V7
 * @return {String} e.g. m OR m7b5 OR Maj7
 */
var getChordName = function (roman) {
    // remove any non character
    var str = roman.replace(/\W/g, '');
    var prefix = 'M';
    // check if it s lowercase
    if (str.toLowerCase() === str) {
        prefix = 'm';
    }
    if (roman.indexOf('°') > -1) {
        return prefix + '7b5';
    }
    if (roman.indexOf('+') > -1) {
        return prefix + '#5';
    }
    if (roman.indexOf('7') > -1) {
        return prefix === 'M' ? 'maj7' : 'm7';
    }
    return prefix;
};
/**
 * Take the specified scale and degrees and return the chord names for them
 * These can be used as the value for the `notes` param of the `clip` method
 * @param {String} noteOctaveScale e.g. 'C4 major'
 * @param  {String} chordDegress e.g. 'I IV V IV'
 * @return {String} e.g. 'CM FM GM FM'
 */
var getChordsByProgression = function (noteOctaveScale, chordDegress) {
    // Set the octave if missing
    // For example if the method was called with `C major` instead of `C4 major`, then add the 4
    var noteOctaveScaleArr = noteOctaveScale.split(' ');
    if (!noteOctaveScaleArr[0].match(/\d/)) {
        noteOctaveScaleArr[0] += '4';
        noteOctaveScale = noteOctaveScaleArr.join(' ');
    }
    // Get the scale from the given note and scale/mode combination
    var mode = (0, harmonics_1.scale)(noteOctaveScale);
    var chordDegreesArr = chordDegress.replace(/\s*,+\s*/g, ' ').split(' ');
    // Now we have something like ['i', 'ii', 'IV']
    // Convert it to a chord family such as ['Cm', 'Dm', 'FM']
    var chordFamily = chordDegreesArr.map(function (roman) {
        var chordName = getChordName(roman); // e.g. m
        // get the index to be used by removing any digit or non alphabet character
        var scaleId = idxByDegree[roman.replace(/\W|\d/g, '').toLowerCase()]; // e.g. 0
        // get the note itself
        var note = mode[scaleId]; // e.g. C
        // get the octave of the note;
        var oct = note.replace(/\D+/, ''); // e.g. 4
        // now get the chord
        return note.replace(/\d/, '') + chordName + '_' + oct;
    });
    return chordFamily.toString().replace(/,/g, ' ');
};
exports.getChordsByProgression = getChordsByProgression;
var getProgFactory = function (_a) {
    var T = _a.T, P = _a.P, D = _a.D;
    return function (count) {
        if (count === void 0) { count = 4; }
        var chords = [];
        // Push root/tonic
        chords.push((0, utils_1.pickOne)(T));
        var i = 1;
        // Pick a predominant
        if (i < count - 1) {
            chords.push((0, utils_1.pickOne)(P));
            i++;
        }
        // Try another predominant
        if (i < count - 1 && (0, utils_1.dice)()) {
            chords.push((0, utils_1.pickOne)(P));
            i++;
        }
        // /////// 4 or more//////////
        if (i < count - 1) {
            // Pick a dominant
            chords.push((0, utils_1.pickOne)(D));
            i++;
        }
        if (i < count - 1) {
            // Pick a predominant
            chords.push((0, utils_1.pickOne)(P));
            i++;
        }
        if (i < count - 1) {
            // Pick a dominant
            chords.push((0, utils_1.pickOne)(D));
            i++;
        }
        // Pick a predominant if possible
        if (i < count - 1 && (0, utils_1.dice)()) {
            chords.push((0, utils_1.pickOne)(P));
            i++;
        }
        // //////////////////////////
        // Fill the rest with dominant
        while (i < count) {
            chords.push((0, utils_1.pickOne)(D));
            i++;
        }
        return chords;
    };
};
var M = getProgFactory({ T: ['I', 'vi'], P: ['ii', 'IV'], D: ['V'] });
var m = getProgFactory({ T: ['i', 'VI'], P: ['ii', 'iv'], D: ['V'] });
/**
 * Generate a chord progression based on basic music theory
 * where we follow tonic to optionally predominant and then dominant
 * and then randomly to predominant and continue this till we reach `count`
 * @param scaleType e.g. M (for major chord progression), m (for minor chord progression)
 * @param count e.g. 4
 */
var progression = function (scaleType, count) {
    if (count === void 0) { count = 4; }
    if (scaleType === 'major' || scaleType === 'M') {
        return M(count);
    }
    if (scaleType === 'minor' || scaleType === 'm') {
        return m(count);
    }
    return [];
};
exports.progression = progression;
